// src/pages/Issues/IssueDetails.jsx
import React, { useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./CounterPartyIssueDetail.css";
import { FiBell } from "react-icons/fi";

import AppLoader from "../../../components/AppLoader/appLoader";
import {
  actionMap,
  formatDuration,
  formatLabel,
  getStatusIcon,
  mapNotificationTypeToColor,
  mapStatusToBootstrap,
  mapStatusToDotColor,
} from "../../../components/Constants/Constants";
import { ArrowLeft, Plus, RotateCw } from "lucide-react";
import { Button, Modal } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";

import { useCounterPartyIssueDetails } from "../../../customHooks/CounterPartyIssues/CounterPartyIssues/useCounterPartyIssueDetails";

const GroupedActionAccordion = ({
  groupedActions,
  activeTab,
  statusFilter,
  openPhase,
  setOpenPhase,
  renderGroupedActionItem,
}) => {
  // 1. Pre-filter the grouped actions based on activeTab and statusFilter
  const filteredGroups = groupedActions
    .map((group) => {
      const actionsFiltered = group.actions.filter(
        (action) =>
          (activeTab === "all" || action.action_type === "RESPONDENT") &&
          (statusFilter === "all" || action?.description?.code === statusFilter)
      );

      return {
        ...group,
        actions: actionsFiltered, // Keep only the matching actions
      };
    })
    .filter((group) => group.actions.length > 0); // 2. Remove groups (phases) that now have zero actions

  if (filteredGroups.length === 0) {
    return (
      <div className="text-center text-muted py-3">
        No actions found matching the current filters.
      </div>
    );
  }

  // 3. Render only the remaining, relevant phases
  return filteredGroups.map((group, groupIdx) => {
    const groupKey = `${group.phase}-${group.level}`;
    return (
      <div
        key={group.phase || groupIdx}
        className="accordion-item mb-3 border-0 shadow-sm rounded-3 overflow-hidden"
      >
        {/* Accordion Header */}
        <div
          onClick={() => setOpenPhase(openPhase === groupKey ? null : groupKey)}
          className={`d-flex justify-content-between align-items-center px-3 py-2 fw-semibold text-dark border rounded-2
          ${
            openPhase === groupKey
              ? "bg-primary text-white"
              : "bg-light text-dark"
          }
          cursor-pointer transition-all`}
          style={{
            cursor: "pointer",
            transition: "background-color 0.2s ease, color 0.2s ease",
          }}
        >
          <span>
            Phase: <strong>{group.phase}</strong> ({group.level})
          </span>
          <i
            className={`bi ${
              openPhase === groupKey ? "bi-chevron-down" : "bi-chevron-right"
            }`}
          ></i>
        </div>

        {/* Accordion Content */}
        {openPhase === groupKey && (
          <div className="accordion-body border-start border-end border-bottom bg-white py-3 px-3">
            {/* Map and render the filtered actions using the passed-in render function */}
            {group.actions.map(renderGroupedActionItem)}
          </div>
        )}
      </div>
    );
  });
};

// Convert ISO duration (e.g., PT2H, P1D) → human readable

function CounterPartyIssueDetails() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const {
    issueData,
    loading,
    error,
    refetch: getIssueDetails,
    formData,
    setFormData,
    handleChange,
    handleResolutionChange,
    handleSave,
    isModalOpen,
    setIsModalOpen,
    modalType,
    setModalType,
    closeModal,
    handleBack,
    isResolutionDisabled,
    resolutionDropdowns,
    handleAddResolution,
    handleRemoveResolution,
    handleBellClick,
    infoRequestType,
    setInfoRequestType,
    selectedPendingRefId,
    setSelectedPendingRefId,
    resolveIssue,
  } = useCounterPartyIssueDetails({ issueId });

  const { addToast } = useToasts();

  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showResolutionHierarchy, setShowResolutionHierarchy] = useState(false);
  const [showInfoHistory, setShowInfoHistory] = useState(false);
  const [openPhase, setOpenPhase] = useState(null); // NEW: State for Accordion control
  // --- END OF HOOKS ---

  // --- 2. EARLY RETURNS (Now safe to use) ---

  const issue = issueData?.issue_details;
  const actions = issueData?.actions || [];
  const orderDetails = issueData?.order_details;
  const availableItems = resolutionDropdowns?.items || [];

  const groupedActions = useMemo(() => {
    // 1. Get the pre-grouped array directly from the API response
    const sourceGroups = issueData?.ModifiedActions;

    if (!sourceGroups || sourceGroups.length === 0) {
      return [];
    }

    // 2. Sort the actions *inside* each group from newest to oldest
    return sourceGroups.map((group) => ({
      ...group, // Keeps the correct phase and level (e.g., "PROCESSING", "GRIEVANCE")
      actions: group.actions
        .slice() // Create a copy to sort safely
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    }));
  }, [issueData?.ModifiedActions]);
  //console.log("showResolutionHierarchy", showResolutionHierarchy);

  const renderGroupedActionItem = useCallback((action, idx) => {
    const actionCode = action.description?.code;
    const map = actionMap[actionCode] || {
      type: actionCode?.replace(/_/g, " ") || "UNKNOWN",
      class: "respondent-action",
    };

    const actorName = action.actor?.type
      ? action.actor.type
          .replace(/_/g, " ") // replace underscores with spaces
          .replace(/\b\w/g, (c) => c.toUpperCase()) // capitalize first letter of each word
      : "INTERFACING NP";
    const actorContact = action.actor?.info?.contact?.phone;
    const actionTime = new Date(action.updatedAt).toLocaleString();
    const actoremail = action.actor?.info?.contact?.email;
    // Get dot color from centralized function
    const dotColor = mapStatusToDotColor(map.type);
    return (
      <div
        key={action.id}
        className={`timeline-item ${map.class}`}
        style={{ "--dot-color": dotColor }}
      >
        <div className="action-header">
          <div className={`action-type ${mapStatusToBootstrap(map.type)}`}>
            {map.type}
          </div>
          <div className="action-time">{actionTime}</div>
        </div>
        {/* <div className="action-desc">{actionDesc}</div> */}
        <div className="action-by">
          <span className="action-person">{actorName}</span>
          <span className="action-contact">
            {actoremail} , {actorContact}
          </span>
        </div>
      </div>
    );
  }, []);
  // -----------------------------------------------------------------------

  const availableResolutionTypes = (
    issueData?.Resolution_dropdowns?.resolutions_types || []
  ).filter(
    (type) => !["PARENT", "RECONCILED", "NOT_RECONCILED"].includes(type)
  );

  const orderInfo = [
    { label: "Network Order ID", value: orderDetails?.ref_id || "N/A" },
    {
      label: "Seller Order ID",
      value: issue?.category?.find((cat) => cat.type === "ORDER")?.id || "N/A",
    },
    { label: "Order Status", value: orderDetails?.status || "N/A" },

    {
      label: "Logistics Provider",
      value: orderDetails?.logistic_provider || "N/A",
    },
    {
      label: "Order Amount",
      value: orderDetails?.order_amount || "N/A",
    },
    {
      label: "Is Order Cancellable?",
      value: orderDetails?.is_cancellable ? "Yes" : "No",
    },
    {
      label: "Is Order Returnable?",
      value: orderDetails?.is_returnable ? "Yes" : "No",
    },
    {
      label: "Return Expiry",
      value: orderDetails?.return_expiry ? orderDetails.return_expiry : "N/A",
    },
    { label: "Buyer Network", value: orderDetails?.buyer_app_uri || "N/A" },
    { label: "Seller Platform", value: orderDetails?.seller_app_uri || "N/A" },
  ];

  const categories = issue?.category || [];

  // Map over the categories to create a new object for each one
  const categoryItems = categories.map((cat) => ({
    // Use the category 'type' (e.g., "ORDER") as the label
    label: cat.type,
    // Use the category 'id' as the value
    value: cat.id || "N/A",
  }));
  const issueClassification = [
    { label: "Level", value: issue?.level || "N/A" },
    ...categoryItems,
    { label: "Issue Code", value: issue?.issue_description?.code || "N/A" },
    {
      label: "Short Description",
      value: issue?.issue_description?.short_desc || "N/A",
    },
    {
      label: "Long Description",
      value: issue?.issue_description?.long_desc || "N/A",
    },
    {
      label: "Created At",
      value: new Date(issue?.created_at).toLocaleString(),
    },
    {
      label: "Updated At",
      value: new Date(issue?.updated_at).toLocaleString(),
    },
  ];

  const issueImages = useMemo(() => {
    const images = issue?.issue_description?.images || [];
    const media = issue?.issue_description?.media || [];

    const allUrls = [
      ...images.map((img) => img.url),
      ...media.map((m) => m.url),
    ].filter(Boolean); // Filter out any null/undefined URLs

    // Return unique URLs
    return [...new Set(allUrls)];
  }, [issue?.issue_description]);

  // Complainant Details

  const complainantDetails = {
    name: issueData?.issue_details?.complainant_details?.name || "N/A",
    org: issueData?.issue_details?.complainant_details?.org_name || "N/A",
    phone: issueData?.issue_details?.complainant_details?.phone || "N/A",
    email: issueData?.issue_details?.complainant_details?.email || "N/A",
  };
  const sourceDetails = {
    name: issueData?.issue_details?.source_details?.name || "N/A",
    org: issueData?.issue_details?.source_details?.org_name || "N/A",
    phone: issueData?.issue_details?.source_details?.phone || "N/A",
    email: issueData?.issue_details?.source_details?.email || "N/A",
  };

  // Timing
  const timingInfo = [
    {
      label: "Expected Response Time",
      value: formatDuration(issue?.expected_response_time),
    },
    {
      label: "Expected Resolution Time",
      value: formatDuration(issue?.expected_resolution_time),
    },
    {
      label: "First Responded At",
      value: issue?.first_responded_at
        ? new Date(issue.first_responded_at).toLocaleString()
        : "N/A",
    },
    {
      label: "Actual Resolution Time",
      value: issue?.resolved_at
        ? new Date(issue.resolved_at).toLocaleString()
        : "N/A",
    },

    { label: "Total Actions", value: actions.length },
  ];
  const isAlreadyEscalated =
    issueData?.issue_details?.status?.toUpperCase() === "ESCALATED" ||
    (issueData?.actions || []).some(
      (action) => action.description?.code === "ESCALATED"
    );
  if (loading) return <AppLoader />;
  if (error) return <div className="text-danger">Error: {error}</div>;
  return (
    <>
      <div className="container-fluid mt-2">
        {/* Header */}
        <div className="header">
          <div className="header-left flex flex-col">
            <div>
              <button
                className="btn btn-outline-secondary me-3"
                onClick={handleBack}
                style={{
                  color: "white",
                  borderColor: "white",
                }}
              >
                <ArrowLeft size={18} className="me-1" />
                Back
              </button>
              Issue #{issue?.id}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              onClick={handleBellClick}
              type="button"
              aria-label="Notifications"
              title="View Info Requests"
            >
              {/* Notification Bell Icon */}
            </span>

            <span
              className={`inline-flex items-center px-3 py-2 rounded ${mapStatusToBootstrap(
                issue?.status
              )}`}
              style={{ gap: "6px" }}
            >
              <span className="text-sm font-medium">
                {(issue?.status || "N/A").replace("_", " ")}
              </span>
            </span>
          </div>
        </div>

        <div className="main-layout">
          <div className="content-area">
            <div className="action-buttons">
              <Button
                variant="primary"
                onClick={resolveIssue}
                disabled={issueData?.disable?.disableResolveIssue}
                title={"Resolve Issue"}
              >
                Resolve Issue
              </Button>
              <Button
                variant="warning"
                onClick={() => {
                  setModalType("requestInfo");
                  setInfoRequestType("new");
                  setSelectedPendingRefId("");
                  setFormData((prev) => ({
                    ...prev,
                    short_desc: "",
                    long_desc: "",
                  }));
                  setShowInfoHistory(false);
                  setIsModalOpen(true);
                }}
                // disabled={issueData?.disable?.disableReqInfo}
                title={
                  issueData?.disable?.disableReqInfo
                    ? "You cannot request more information at this stage but can see the Info History."
                    : "Request more information from the other party."
                }
              >
                Request More Info
              </Button>

              <Button
                variant="success"
                onClick={() => {
                  setModalType("provideResolution");

                  const initialResolutions = availableItems.map((item) => ({
                    ref_type: "ITEM",
                    ref_id: item,
                    resolutions: [
                      {
                        type: "",
                        short_desc: "",
                        amount: "",
                        expected_delivery: "",
                      },
                    ],
                  }));

                  setFormData((prev) => ({
                    ...prev,
                    resolutions: initialResolutions,
                  }));

                  setIsModalOpen(true);
                }}
                disabled={issueData?.disable?.disableProvideRes}
                title={
                  issueData?.disable?.disableProvideRes
                    ? "You cannot provide a resolution at this time."
                    : "Provide a new resolution proposal."
                }
              >
                Send Resolution
              </Button>
            </div>

            <div className="info-section">
              <div className="section-header">Issue Classification</div>
              <div className="compact-grid">
                {issueClassification.map((item) => (
                  <div className="info-item" key={item.label}>
                    <div className="info-label">{item.label}</div>

                    <div
                      className="info-value truncate-text"
                      title={item.value}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}

                {/* --- NEW BLOCK FOR IMAGES --- */}
                {issueImages.length > 0 && (
                  <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                    {" "}
                    {/* Span full width */}
                    <div className="info-label">Attached Media</div>
                    <div className="info-value d-flex flex-wrap gap-2 mt-1">
                      {issueImages.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          title="Click to view full image and download"
                          style={{ textDecoration: "none" }}
                        >
                          <img
                            src={url}
                            alt={`Issue Media ${index + 1}`}
                            style={{
                              width: "60px",
                              height: "60px",
                              objectFit: "cover",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {/* --- END OF NEW BLOCK --- */}
              </div>
            </div>

            <div className="info-section">
              <div className="section-header">Order Information</div>
              <div className="compact-grid">
                {orderInfo.map((item) => (
                  <div className="info-item" key={item.label}>
                    <div className="info-label">{item.label}</div>
                    <div
                      className={`info-value ${
                        item.highlight ? "highlight" : ""
                      } truncate-text`}
                      title={item.value}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Item Details Table */}
              {orderDetails?.items?.length > 0 && (
                <div className="order-items-section mt-3">
                  <div className="section-header">Item Details</div>
                  <div
                    style={{
                      maxHeight: "120px", // roughly 2 rows
                      overflowY: "auto",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                    }}
                  >
                    <table
                      className="table table-bordered mb-0"
                      style={{ fontSize: "0.875rem" }}
                    >
                      <thead className="table-light text-center">
                        <tr>
                          <th style={{ padding: "0.25rem 0.5rem" }}>
                            Product Name
                          </th>
                          <th style={{ padding: "0.25rem 0.5rem" }}>
                            Quantity
                          </th>
                          <th style={{ padding: "0.25rem 0.5rem" }}>Price</th>
                        </tr>
                      </thead>
                      <tbody className="text-center">
                        {orderDetails.items.map((item) => (
                          <tr key={item._id}>
                            <td style={{ padding: "0.25rem 0.5rem" }}>
                              {item.product_name}
                            </td>
                            <td style={{ padding: "0.25rem 0.5rem" }}>
                              {item.quantity}
                            </td>
                            <td style={{ padding: "0.25rem 0.5rem" }}>
                              {item.price}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Complainant */}
            <div className="info-section mb-3">
              <div className="section-header mb-3">Complainant Details</div>
              <div className="d-flex flex-wrap border p-3 rounded">
                {/* Name */}
                <div className="me-5 info-item">
                  <div className="info-label">Name</div>
                  <div className="info-value">{complainantDetails.name}</div>
                </div>

                {/* Organization */}
                <div className="me-5 info-item">
                  <div className="info-label">Organization</div>
                  <div className="info-value">{complainantDetails.org}</div>
                </div>

                {/* Phone */}
                <div className="me-5 info-item">
                  <div className="info-label">Phone</div>
                  <div className="info-value">{complainantDetails.phone}</div>
                </div>

                {/* Email */}
                <div className="me-5 info-item">
                  <div className="info-label">Email</div>
                  <div className="info-value">{complainantDetails.email}</div>
                </div>
              </div>
            </div>

            {/* --- Consumer Details Section --- */}
            <div className="info-section mb-3">
              <div className="section-header mb-3">Consumer Details</div>
              <div className="d-flex flex-wrap border p-3 rounded">
                {/* Name */}
                <div className="me-5 info-item">
                  <div className="info-label">Name</div>
                  <div className="info-value">{sourceDetails.name}</div>
                </div>

                {/* Organization */}
                <div className="me-5 info-item">
                  <div className="info-label">Organization</div>
                  <div
                    className="info-value truncate-text"
                    title={sourceDetails.org}
                  >
                    {sourceDetails.org}
                  </div>
                </div>

                {/* Phone */}
                <div className="me-5 info-item">
                  <div className="info-label">Phone</div>
                  <div className="info-value">{sourceDetails.phone}</div>
                </div>

                {/* Email */}
                <div className="me-5 info-item">
                  <div className="info-label">Email</div>
                  <div
                    className="info-value truncate-text"
                    title={sourceDetails.email}
                  >
                    {sourceDetails.email}
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="section-header">Response & Resolution Timing</div>
              <div className="compact-grid">
                {timingInfo.map((item) => (
                  <div className="info-item" key={item.label}>
                    <div className="info-label">{item.label}</div>
                    <div className="info-value">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Timeline Area */}
          <div className="timeline">
            <div className="timeline-header">Action Timeline</div>
            <div className="timeline-filters flex items-center gap-3 mb-3">
              {/* Tabs */}
              {!showResolutionHierarchy && (
                <div className="timeline-tabs flex gap-2">
                  <div
                    className={`tab ${activeTab === "all" ? "active" : ""}`}
                    onClick={() => setActiveTab("all")}
                  >
                    All
                  </div>

                  <div
                    className={`tab ${
                      activeTab === "respondent" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("respondent")}
                  >
                    Respondent
                  </div>

                  {/* Status Filter Dropdown */}
                  <div className="dropdown-filters">
                    <select
                      className="form-control"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      {[
                        ...new Set(
                          // Flatten actions from all groups, then filter by activeTab
                          groupedActions
                            .flatMap((group) => group.actions)
                            .filter((a) =>
                              activeTab === "all"
                                ? true
                                : a.action_type === "RESPONDENT"
                            )
                            .map((a) => a.description?.code)
                            .filter(Boolean)
                        ),
                      ].map((status) => (
                        <option key={status} value={status}>
                          {formatLabel(status)}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setStatusFilter("all")}
                      disabled={statusFilter === "all"}
                      style={{ padding: "0.15rem 0.5rem" }}
                      title="Clear Status Filter"
                    >
                      <RotateCw size={"16px"} />
                    </Button>
                  </div>
                </div>
              )}

              {/* Resolution toggle button */}
              <div className="dropdown-filters flex gap-2">
                <button
                  className={`btn btn-outline-primary btn-sm ${
                    showResolutionHierarchy ? "active" : ""
                  }`}
                  onClick={() => setShowResolutionHierarchy((prev) => !prev)}
                >
                  {showResolutionHierarchy
                    ? "Hide Resolution Hierarchy"
                    : "Show by Resolution"}
                </button>
              </div>
            </div>

            <div
              className="timeline" // Changed outer div class for clarity if needed
              style={{ maxHeight: "800px", overflowY: "auto" }}
            >
              {showResolutionHierarchy ? (
                issueData?.resolutions
                  .filter(
                    (block) =>
                      block.item_id ||
                      (block.resolutions && block.resolutions.length > 0)
                  )
                  ?.map((itemBlock, itemIdx) => (
                    <div
                      key={itemBlock.item_id || itemIdx}
                      className="item-resolution-block mb-4"
                    >
                      {itemBlock.item_id !== "GENERAL" && (
                        <span
                          style={{ fontSize: "1rem" }}
                          className="item-header font-semibold mb-3"
                        >
                          Item ID:{" "}
                          <span className="text-primary">
                            {itemBlock.item_id}
                          </span>
                        </span>
                      )}

                      {itemBlock.resolutions?.map((res, idx) => {
                        const isMultiOption = !!res.parent_details;
                        const finalResolution =
                          res.resolution_history?.[0] || {};
                        const hasFinalOutcome = !!finalResolution.status;
                        const isAccepted =
                          finalResolution.status === "RESOLUTION_ACCEPTED";

                        const parent = res.parent_details || res;
                        const options = res.options || [];

                        const proposedBy =
                          parent.proposed_by?.info?.person?.name || "Unknown";
                        const proposedByOrg =
                          parent.proposed_by?.info?.org?.name || "Unknown Org";
                        const proposedAt = parent.proposed_at
                          ? new Date(parent.proposed_at).toLocaleDateString()
                          : "N/A";

                        return (
                          <div key={res.id || idx} className="timeline-item">
                            <div
                              className={`timeline-dot ${
                                isAccepted ? "accepted-dot" : "rejected-dot"
                              }`}
                            ></div>

                            <div className="card">
                              <div className="card-header flex items-center justify-between">
                                <h4 className="text-base font-semibold">
                                  Resolution #{idx + 1}
                                </h4>

                                {hasFinalOutcome && (
                                  <span
                                    className={`status ${
                                      isAccepted ? "accepted" : "rejected"
                                    }`}
                                  >
                                    {isAccepted ? "✅ Accepted" : "❌ Rejected"}
                                  </span>
                                )}
                              </div>

                              <div className="card-body">
                                <div className="meta-info mb-2">
                                  <span>
                                    <strong>Proposed By:</strong> {proposedBy} (
                                    {proposedByOrg})
                                  </span>
                                  <span>
                                    <strong>Proposed On:</strong> {proposedAt}
                                  </span>
                                  <span>
                                    <strong>Hierarchy Type:</strong>{" "}
                                    {isMultiOption
                                      ? "Multi-Option Proposal"
                                      : "Direct Proposal"}
                                  </span>
                                </div>

                                {isMultiOption ? (
                                  <div className="options-list">
                                    <h4>Options Presented:</h4>
                                    {options.map((opt) => (
                                      <div key={opt.id} className="option">
                                        <p>
                                          {formatLabel(opt.description.code)}
                                        </p>
                                        <div className="detail">
                                          {opt.description.short_desc && (
                                            <>{opt.description.short_desc}.</>
                                          )}
                                          {opt.description.amount && (
                                            <>
                                              {" "}
                                              Amount: ₹{opt.description.amount}.
                                            </>
                                          )}
                                          {opt.description
                                            .expected_delivery && (
                                            <>
                                              {" "}
                                              Expected Delivery:{" "}
                                              {new Date(
                                                opt.description.expected_delivery
                                              ).toLocaleDateString()}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="proposal-details">
                                    <h4>Proposal Details:</h4>
                                    <div className="option">
                                      <p>Offer {res.description.code}</p>
                                      <div className="detail">
                                        {res.description.short_desc && (
                                          <>{res.description.short_desc}.</>
                                        )}
                                        {res.description.amount && (
                                          <>
                                            {" "}
                                            Amount: ₹{res.description.amount}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {hasFinalOutcome && (
                                <div
                                  className={`card-footer ${
                                    isAccepted
                                      ? "footer-accepted"
                                      : "footer-rejected"
                                  }`}
                                >
                                  Final Outcome:{" "}
                                  {isAccepted ? "Accepted the" : "Rejected the"}{" "}
                                  <strong>
                                    {finalResolution.description?.code}
                                  </strong>{" "}
                                  {finalResolution.description?.amount && (
                                    <strong>
                                      ₹{finalResolution.description.amount}
                                    </strong>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
              ) : (
                <GroupedActionAccordion
                  groupedActions={groupedActions}
                  activeTab={activeTab}
                  statusFilter={statusFilter}
                  openPhase={openPhase}
                  setOpenPhase={setOpenPhase}
                  renderGroupedActionItem={renderGroupedActionItem}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={isModalOpen}
        onHide={closeModal}
        centered={modalType !== "viewInfoRequests"}
        dialogClassName={
          modalType === "viewInfoRequests" ? "notification-panel" : ""
        }
        backdropClassName={
          modalType === "viewInfoRequests" ? "notification-backdrop" : ""
        }
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-xl font-semibold text-gray-800">
            {modalType === "requestInfo" && "Request More Information"}
            {modalType === "provideResolution" && "Send Resolution"}
            {modalType === "viewInfoRequests" && "Info Requests"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave({ onClose: closeModal, addToast });
            }}
          >
            {modalType === "viewInfoRequests" && (
              // This div creates the scrollable area with a maximum height
              <div style={{ maxHeight: "60vh" }} className="overflow-y-auto">
                {issueData?.Notifications?.length > 0 ? (
                  issueData.Notifications.map((item) => (
                    <div
                      key={item.id}
                      className="notification-item border rounded-lg p-3 mb-3 bg-gray-50 hover:bg-gray-100 transition"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h6 className="font-semibold text-gray-800">
                            <strong>Short Description:</strong>{" "}
                            {item.short_description}
                          </h6>
                          <p className="text-sm text-gray-600">
                            <strong>Long Description:</strong>
                            {item.long_description}
                          </p>
                        </div>
                        <span className="text-sm text-gray-600">
                          <strong>Updated At:</strong>
                          {new Date(item.updatedAt).toLocaleString()}
                        </span>
                      </div>

                      <div className="mt-2 text-xs text-gray-700">
                        {(item.actor_person_name || item.actor_org_name) && (
                          <p>
                            <strong>By:</strong>{" "}
                            {item.actor_person_name || "N/A"}
                            {item.actor_org_name && ` (${item.actor_org_name})`}
                          </p>
                        )}

                        {item.actor_contact_email && (
                          <p>
                            <strong>Email:</strong> {item.actor_contact_email}
                          </p>
                        )}
                        {item.actor_contact_phone && (
                          <p>
                            <strong>Phone:</strong> {item.actor_contact_phone}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-3">
                    No Info Requests Found
                  </p>
                )}
              </div>
            )}

            {modalType === "requestInfo" && (
              <>
                {(() => {
                  const isReqInfoDisabled = issueData?.disable?.disableReqInfo;
                  return (
                    <>
                      {isReqInfoDisabled && (
                        <div
                          className="alert alert-warning text-center small p-2 mb-3"
                          role="alert"
                        >
                          You cannot request more information at this stage but
                          can see the Info History.
                        </div>
                      )}

                      {issueData?.RequestedInfoItems?.PendingInfoRequests >
                        0 && (
                        <div className="row align-items-center mb-3">
                          <label className="col-sm-4 col-form-label fw-medium">
                            Request Type
                          </label>
                          <div className="col-sm-8">
                            <div className="d-flex flex-row align-items-center gap-4">
                              <label
                                className="form-check-label d-flex align-items-center"
                                style={{ cursor: "pointer" }}
                              >
                                <input
                                  type="radio"
                                  name="infoRequestType"
                                  value="new"
                                  checked={infoRequestType === "new"}
                                  onChange={(e) => {
                                    setInfoRequestType(e.target.value);
                                    setSelectedPendingRefId(""); // Reset selection
                                  }}
                                  className="form-check-input me-2"
                                  disabled={isReqInfoDisabled} // <-- APPLIED
                                />
                                Create New
                              </label>

                              <label
                                className="form-check-label d-flex align-items-center"
                                style={{ cursor: "pointer" }}
                              >
                                <input
                                  type="radio"
                                  name="infoRequestType"
                                  value="refer"
                                  checked={infoRequestType === "refer"}
                                  onChange={(e) =>
                                    setInfoRequestType(e.target.value)
                                  }
                                  className="form-check-input me-2"
                                  disabled={isReqInfoDisabled} // <-- APPLIED
                                />
                                Refer to Pending
                              </label>
                            </div>

                            {/* Dropdown appears below the radio buttons when 'refer' is selected */}
                            {infoRequestType === "refer" && (
                              <select
                                className="form-select mt-2" // Added margin-top for spacing
                                value={selectedPendingRefId}
                                onChange={(e) =>
                                  setSelectedPendingRefId(e.target.value)
                                }
                                required
                                disabled={isReqInfoDisabled} // <-- APPLIED
                              >
                                <option value="">
                                  Select a Pending Request...
                                </option>
                                {issueData?.RequestedInfoItems?.RequestedInfoDescriptions?.map(
                                  (info) => (
                                    <option key={info.id} value={info.id}>
                                      {info.description}
                                    </option>
                                  )
                                )}
                              </select>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Short Description Field */}
                      <div className="row align-items-center mb-3">
                        <label className="col-sm-4 col-form-label fw-medium">
                          Short Description{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="col-sm-8">
                          <input
                            type="text"
                            name="short_desc"
                            value={formData.short_desc}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Enter short description"
                            required
                            disabled={isReqInfoDisabled} // <-- APPLIED
                          />
                        </div>
                      </div>

                      {/* Long Description Field */}
                      <div className="row align-items-center mb-3">
                        <label className="col-sm-4 col-form-label fw-medium">
                          Long Description
                        </label>
                        <div className="col-sm-8">
                          <textarea
                            name="long_desc"
                            value={formData.long_desc}
                            onChange={handleChange}
                            rows={2}
                            className="form-control"
                            placeholder="Enter detailed description"
                            required
                            disabled={isReqInfoDisabled} // <-- APPLIED
                          />
                        </div>
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            {modalType === "provideResolution" && (
              <>
                <p className="text-center text-muted small fst-italic mb-3">
                  You can provide up to 3 resolution options for each item.
                </p>

                {/* 👇 Scrollable container */}
                <div
                  className="overflow-auto border rounded p-2"
                  style={{ maxHeight: "70vh" }}
                >
                  {formData.resolutions?.map((itemGroup, itemIdx) => (
                    <div
                      key={itemIdx}
                      className="card mb-4 p-3 border-primary bg-white shadow-sm"
                    >
                      <h6 className="fw-semibold text-primary mb-2">
                        Item: {itemGroup.ref_id}
                      </h6>
                      {itemGroup.resolutions?.map((res, resIdx) => (
                        <div
                          key={resIdx}
                          className="border-start ps-3 mb-3 pt-2 bg-light rounded position-relative p-2"
                        >
                          {itemGroup.resolutions.length > 1 && (
                            <button
                              type="button"
                              style={{
                                transform: "scale(0.5)",
                              }}
                              className="btn-close position-absolute top-0 end-0 me-2 mt-1"
                              onClick={() =>
                                handleRemoveResolution(itemIdx, resIdx)
                              }
                              title="Remove this resolution option"
                            ></button>
                          )}
                          <div className="row align-items-center mb-2">
                            <label className="col-sm-4 col-form-label fw-medium">
                              Resolution Type
                            </label>
                            <div className="col-sm-8">
                              <select
                                className="form-control form-control-sm"
                                value={res.type || ""}
                                onChange={(e) =>
                                  handleResolutionChange(
                                    itemIdx,
                                    resIdx,
                                    "type",
                                    e.target.value
                                  )
                                }
                                required
                              >
                                <option value="">Select Type</option>
                                {availableResolutionTypes.map((type) => (
                                  <option key={type} value={type}>
                                    {type.replace("_", " ")}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="row align-items-center mb-2">
                            <label className="col-sm-4 col-form-label fw-medium">
                              Short Description{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div className="col-sm-8">
                              <input
                                type="text"
                                value={res.short_desc || ""}
                                onChange={(e) =>
                                  handleResolutionChange(
                                    itemIdx,
                                    resIdx,
                                    "short_desc",
                                    e.target.value
                                  )
                                }
                                className="form-control form-control-sm"
                                placeholder="Enter short description"
                                required
                              />
                            </div>
                          </div>

                          {res.type === "REFUND" && (
                            <div className="row align-items-center mb-2">
                              <label className="col-sm-4 col-form-label fw-medium text-danger">
                                Amount
                              </label>
                              <div className="col-sm-8">
                                <input
                                  type="number"
                                  value={res.amount || ""}
                                  onChange={(e) =>
                                    handleResolutionChange(
                                      itemIdx,
                                      resIdx,
                                      "amount",
                                      e.target.value
                                    )
                                  }
                                  className="form-control form-control-sm"
                                  placeholder="Enter amount"
                                  required
                                />
                              </div>
                            </div>
                          )}

                          {(res.type === "NO_ACTION" ||
                            res.type === "REPLACEMENT") && (
                            <div className="row align-items-center mb-2">
                              <label className="col-sm-4 col-form-label fw-medium text-info">
                                Expected Delivery
                              </label>
                              <div className="col-sm-8">
                                <input
                                  type="date"
                                  value={res.expected_delivery || ""}
                                  onChange={(e) =>
                                    handleResolutionChange(
                                      itemIdx,
                                      resIdx,
                                      "expected_delivery",
                                      e.target.value
                                    )
                                  }
                                  className="form-control form-control-sm"
                                  required
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleAddResolution(itemIdx)}
                        >
                          + Add Resolution
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </form>
        </Modal.Body>

        {/* Modal Footer */}
        {modalType !== "viewInfoRequests" && (
          <Modal.Footer className="modal-footer-custom d-flex justify-content-between">
            <div className="footer-left">
              {modalType === "requestInfo" && (
                <Button
                  variant="outline-info"
                  size="md"
                  onClick={() => setShowInfoHistory((prev) => !prev)}
                >
                  {showInfoHistory ? "Hide Info History" : "Show Info History"}
                </Button>
              )}
            </div>

            <div className="footer-right">
              <Button
                variant="secondary"
                onClick={closeModal}
                className="cancel-btn me-2"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleSave({ onClose: closeModal, addToast })}
                className="save-btn"
                disabled={
                  (modalType === "requestInfo" &&
                    issueData?.disable?.disableReqInfo) ||
                  (modalType === "provideResolution" &&
                    issueData?.disable?.disableProvideRes)
                }
                title={
                  modalType === "requestInfo" &&
                  issueData?.disable?.disableReqInfo
                    ? "You cannot request more information at this stage."
                    : modalType === "provideResolution" &&
                      issueData?.disable?.disableProvideRes
                    ? "You cannot provide a resolution at this time."
                    : "Send"
                }
              >
                Send
              </Button>
            </div>
          </Modal.Footer>
        )}

        {showInfoHistory && modalType === "requestInfo" && (
          <div
            className="mb-3 border rounded p-2 bg-light mx-3"
            style={{ maxHeight: "200px", overflowY: "auto" }}
          >
            {issueData?.Notifications?.length > 0 ? (
              issueData.Notifications.map((item) => {
                const issueImages = item?.images || [];
                const issueMedia = item?.media || [];
                const allMedia = [...issueImages, ...issueMedia]; // combined

                return (
                  <div
                    key={item.id}
                    className="notification-item border rounded-lg p-3 mb-2 bg-white shadow-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h6 className="font-semibold text-gray-800">
                          <strong>Short Description:</strong>{" "}
                          {item.short_description || "N/A"}
                        </h6>
                        <p className="text-sm text-gray-600">
                          <strong>Long Description:</strong>{" "}
                          {item.long_description || "N/A"}
                        </p>
                      </div>

                      <span
                        className="text-sm text-gray-600"
                        style={{ minWidth: "150px", textAlign: "right" }}
                      >
                        <strong>Updated At:</strong>

                        {item.updatedAt
                          ? new Date(item.updatedAt).toLocaleString()
                          : "N/A"}
                      </span>
                    </div>

                    {/* Actor Details */}
                    <div className="mt-2 text-xs text-gray-700">
                      {(item.actor_person_name || item.actor_org_name) && (
                        <p>
                          <strong>By:</strong> {item.actor_person_name || "N/A"}
                          {item.actor_org_name && ` (${item.actor_org_name})`}
                        </p>
                      )}

                      {item.actor_contact_phone && (
                        <p>
                          <strong>Phone:</strong> {item.actor_contact_phone}
                        </p>
                      )}

                      {item.actor_contact_email && (
                        <p>
                          <strong>Email:</strong> {item.actor_contact_email}
                        </p>
                      )}
                    </div>

                    {/* Attached Media */}
                    {allMedia.length > 0 && (
                      <div
                        className="info-item mt-3"
                        style={{ gridColumn: "1 / -1" }}
                      >
                        <div className="info-label font-semibold mb-1">
                          Attached Media
                        </div>
                        <div className="info-value d-flex flex-wrap gap-2 mt-1">
                          {allMedia.map((url, index) => (
                            <a
                              key={index}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              title="Click to view full image and download"
                              style={{ textDecoration: "none" }}
                            >
                              <img
                                src={url}
                                alt={`Media ${index + 1}`}
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                }}
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-center text-gray-500 py-3">
                No Info Requests Found
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

export default CounterPartyIssueDetails;
